import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { AuthProvider } from "@/hooks/useAuth";
import ProtectedRoute from "@/components/ProtectedRoute";
import BillingGate from "@/components/shared/BillingGate";
import { InternalStaffProvider } from "@/contexts/InternalStaffContext";
import I18nDebugBanner from "@/components/shared/I18nDebugBanner";
import PWAInstallPrompt from "@/components/shared/PWAInstallPrompt";
import PWAUpdatePrompt from "@/components/shared/PWAUpdatePrompt";
import { lazy, Suspense, useEffect, useState } from "react";
import { Capacitor } from "@capacitor/core";
import { UserModeProvider } from "@/components/mobile/personalization/UserModeContext";
import { DemoModeProvider } from "@/components/mobile/demo";
import { WellebitBluetoothProvider } from "@/contexts/WellebitBluetoothContext";
import { NativeHealthKitProvider } from "@/contexts/NativeHealthKitContext";
import { GlassTintProvider } from "@/contexts/GlassTintContext";
import ErrorBoundary from "@/components/ErrorBoundary";

// Detect if running as native app
const isNativePlatform = Capacitor.isNativePlatform();

// Mobile companion app (standalone entry)
const MobileAppRoot = lazy(() => import("./components/mobile/MobileAppRoot"));

// Lazy load pages
const Index = lazy(() => import("./pages/Index"));
const Auth = lazy(() => import("./pages/Auth"));
const CareHomes = lazy(() => import("./pages/CareHomes"));
const Families = lazy(() => import("./pages/Families"));
const Medical = lazy(() => import("./pages/Medical"));
const Workplace = lazy(() => import("./pages/Workplace"));
const Sports = lazy(() => import("./pages/Sports"));
const OilGas = lazy(() => import("./pages/OilGas"));
const Users = lazy(() => import("./pages/Users"));
const ClinicalAdmin = lazy(() => import("./pages/ClinicalAdmin"));
const ClinicalProtocols = lazy(() => import("./pages/ClinicalProtocols"));
const ClinicalInvite = lazy(() => import("./pages/ClinicalInvite"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Onboarding = lazy(() => import("./pages/Onboarding"));
const OrganisationSettings = lazy(() => import("./pages/OrganisationSettings"));
const SubscriptionPage = lazy(() => import("./pages/SubscriptionPage"));
const AdminPortal = lazy(() => import("./pages/AdminPortal"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const ExecutiveReport = lazy(() => import("./pages/ExecutiveReport"));
const ExecutiveBoardReport = lazy(() => import("./pages/ExecutiveBoardReport"));
const ShowcaseLanding = lazy(() => import("./pages/ShowcaseLanding"));
const Capture = lazy(() => import("./pages/Capture"));
const TalentLanding = lazy(() => import("./pages/landing/TalentLanding"));
const Talent = lazy(() => import("./pages/Talent"));
const MarketingHome = lazy(() => import("./pages/marketing/Home"));
const MarketingProduct = lazy(() => import("./pages/marketing/Product"));
const MarketingSKUs = lazy(() => import("./pages/marketing/SKUs"));
const MarketingAbout = lazy(() => import("./pages/marketing/About"));
const MarketingContact = lazy(() => import("./pages/marketing/Contact"));
const AthleteConsent = lazy(() => import("./pages/AthleteConsent"));
const TalentShareView = lazy(() => import("./pages/TalentShareView"));

const queryClient = new QueryClient();

// Branded splash screen for native builds
const NativeDebugSplash = ({ children }: { children: React.ReactNode }) => {
  const [showSplash, setShowSplash] = useState(isNativePlatform);
  const [fadeOut, setFadeOut] = useState(false);

  useEffect(() => {
    if (!isNativePlatform) return;
    // Start fade-out after 2s, then remove after animation
    const fadeTimer = setTimeout(() => setFadeOut(true), 2000);
    const hideTimer = setTimeout(() => setShowSplash(false), 2600);
    return () => { clearTimeout(fadeTimer); clearTimeout(hideTimer); };
  }, []);

  if (showSplash) {
    return (
      <div
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 99999,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'hsl(0,0%,4%)',
          transition: 'opacity 0.6s ease-out',
          opacity: fadeOut ? 0 : 1,
          fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", system-ui, sans-serif',
        }}
      >
        {/* Ambient glow */}
        <div style={{
          position: 'absolute',
          width: '300px',
          height: '300px',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(14,165,233,0.15) 0%, transparent 70%)',
          filter: 'blur(40px)',
          animation: 'splashGlow 3s ease-in-out infinite',
        }} />

        {/* Droplet icon */}
        <div style={{
          width: '128px',
          height: '128px',
          marginBottom: '28px',
          animation: 'splashDroplet 1.2s ease-out',
          filter: 'drop-shadow(0 0 24px rgba(14,165,233,0.4))',
        }}>
          <img
            src="/app-icon.png"
            alt="Wellebit"
            style={{ width: '100%', height: '100%', objectFit: 'contain' }}
          />
        </div>

        {/* Brand name */}
        <h1 style={{
          fontSize: '28px',
          fontWeight: 700,
          letterSpacing: '-0.02em',
          color: '#ffffff',
          margin: 0,
          animation: 'splashFadeUp 0.8s ease-out 0.3s both',
        }}>
          Wellebit
        </h1>
        <p style={{
          fontSize: '14px',
          fontWeight: 500,
          letterSpacing: '0.15em',
          textTransform: 'uppercase',
          color: 'rgba(14,165,233,0.8)',
          margin: '6px 0 0 0',
          animation: 'splashFadeUp 0.8s ease-out 0.5s both',
        }}>
          Smart Hydration
        </p>

        {/* Loading indicator */}
        <div style={{
          position: 'absolute',
          bottom: '80px',
          width: '32px',
          height: '3px',
          borderRadius: '2px',
          backgroundColor: 'rgba(255,255,255,0.1)',
          overflow: 'hidden',
          animation: 'splashFadeUp 0.8s ease-out 0.7s both',
        }}>
          <div style={{
            width: '50%',
            height: '100%',
            borderRadius: '2px',
            background: 'linear-gradient(90deg, #0ea5e9, #22d3ee)',
            animation: 'splashLoader 1.2s ease-in-out infinite',
          }} />
        </div>

        <style>{`
          @keyframes splashGlow {
            0%, 100% { transform: scale(1); opacity: 0.6; }
            50% { transform: scale(1.15); opacity: 1; }
          }
          @keyframes splashDroplet {
            0% { transform: translateY(-30px) scale(0.7); opacity: 0; }
            60% { transform: translateY(4px) scale(1.02); opacity: 1; }
            100% { transform: translateY(0) scale(1); opacity: 1; }
          }
          @keyframes splashFadeUp {
            from { transform: translateY(12px); opacity: 0; }
            to { transform: translateY(0); opacity: 1; }
          }
          @keyframes splashLoader {
            0% { transform: translateX(-100%); }
            100% { transform: translateX(200%); }
          }
        `}</style>
      </div>
    );
  }

  return <>{children}</>;
};

// Loading fallback for lazy-loaded pages
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="flex flex-col items-center gap-4">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      <p className="text-muted-foreground text-sm">Loading...</p>
    </div>
  </div>
);

const App = () => (
  <ErrorBoundary>
    <NativeDebugSplash>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider attribute="class" defaultTheme="light" enableSystem={true}>
          <TooltipProvider>
            <Toaster />
            <Sonner />
            <I18nDebugBanner />
            <PWAInstallPrompt />
            <PWAUpdatePrompt />
            <BrowserRouter>
              <AuthProvider>
                <InternalStaffProvider>
                <Suspense fallback={<PageLoader />}>
                  <Routes>
                    {/* Public marketing site */}
                    <Route path="/" element={isNativePlatform ? <Navigate to="/mobile" replace /> : <MarketingHome />} />
                    <Route path="/product" element={<MarketingProduct />} />
                    <Route path="/skus" element={<MarketingSKUs />} />
                    <Route path="/about" element={<MarketingAbout />} />
                    <Route path="/contact" element={<MarketingContact />} />

                    {/* Deep link routes for QR code connections */}
                    <Route path="/connect" element={<Navigate to="/mobile" replace />} />
                    <Route path="/join" element={<Navigate to="/mobile" replace />} />
                    
                    <Route path="/mobile/*" element={<GlassTintProvider><UserModeProvider><WellebitBluetoothProvider><NativeHealthKitProvider><DemoModeProvider><MobileAppRoot /></DemoModeProvider></NativeHealthKitProvider></WellebitBluetoothProvider></UserModeProvider></GlassTintProvider>} />
                    <Route path="/auth" element={<Auth />} />
                    <Route path="/reset-password" element={<ResetPassword />} />
                    <Route path="/app/onboarding" element={<ProtectedRoute><Onboarding /></ProtectedRoute>} />
                    
                    {/* Staff Admin Portal */}
                    <Route path="/app/admin" element={<ProtectedRoute><AdminPortal /></ProtectedRoute>} />
                    
                    {/* Protected app routes - all under /app */}
                    <Route path="/app" element={<ProtectedRoute><BillingGate><Index /></BillingGate></ProtectedRoute>} />
                    <Route path="/app/care" element={<ProtectedRoute><BillingGate><CareHomes /></BillingGate></ProtectedRoute>} />
                    <Route path="/app/family" element={<ProtectedRoute><BillingGate><Families /></BillingGate></ProtectedRoute>} />
                    <Route path="/app/medical" element={<ProtectedRoute><BillingGate><Medical /></BillingGate></ProtectedRoute>} />
                    <Route path="/app/workplace" element={<ProtectedRoute><BillingGate><Workplace /></BillingGate></ProtectedRoute>} />
                    <Route path="/app/sports" element={<ProtectedRoute><BillingGate><Sports /></BillingGate></ProtectedRoute>} />
                    <Route path="/app/oilgas" element={<ProtectedRoute><BillingGate><OilGas /></BillingGate></ProtectedRoute>} />
                    <Route path="/talent" element={<TalentLanding />} />
                    <Route path="/app/talent" element={<ProtectedRoute><Talent /></ProtectedRoute>} />
                    <Route path="/app/talent/me" element={<ProtectedRoute><AthleteConsent /></ProtectedRoute>} />
                    <Route path="/app/users" element={<ProtectedRoute><BillingGate><Users /></BillingGate></ProtectedRoute>} />
                    <Route path="/app/clinical-admin" element={<ProtectedRoute><BillingGate><ClinicalAdmin /></BillingGate></ProtectedRoute>} />
                    <Route path="/app/clinical-protocols" element={<ProtectedRoute><BillingGate><ClinicalProtocols /></BillingGate></ProtectedRoute>} />
                    <Route path="/clinical-invite/:token" element={<ClinicalInvite />} />
                    <Route path="/app/executive-report" element={<ProtectedRoute><BillingGate><ExecutiveReport /></BillingGate></ProtectedRoute>} />
                    <Route path="/app/board-report" element={<ProtectedRoute><BillingGate><ExecutiveBoardReport /></BillingGate></ProtectedRoute>} />
                    <Route path="/app/org-settings" element={<ProtectedRoute><BillingGate><OrganisationSettings /></BillingGate></ProtectedRoute>} />
                    <Route path="/app/subscription" element={<ProtectedRoute><SubscriptionPage /></ProtectedRoute>} />
                    
                    {/* Legacy route redirects — preserve old bookmarks */}
                    <Route path="/care" element={<Navigate to="/app/care" replace />} />
                    <Route path="/family" element={<Navigate to="/app/family" replace />} />
                    <Route path="/womenshealth" element={<Navigate to="/app/medical" replace />} />
                    <Route path="/medical" element={<Navigate to="/app/medical" replace />} />
                    <Route path="/workplace" element={<Navigate to="/app/workplace" replace />} />
                    <Route path="/sports" element={<Navigate to="/app/sports" replace />} />
                    <Route path="/oilgas" element={<Navigate to="/app/oilgas" replace />} />
                    <Route path="/users" element={<Navigate to="/app/users" replace />} />
                    <Route path="/executive-report" element={<Navigate to="/app/executive-report" replace />} />
                    <Route path="/board-report" element={<Navigate to="/app/board-report" replace />} />
                    
                    {/* Showcase landing (public) */}
                    <Route path="/showcase" element={<ShowcaseLanding />} />

                    {/* Headless capture surfaces for marketing video screenshots */}
                    <Route path="/capture/:id" element={<Capture />} />

                    {/* Public Talent share-link viewer (no auth) */}
                    <Route path="/share/talent/:token" element={<TalentShareView />} />

                    {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </Suspense>
                </InternalStaffProvider>
              </AuthProvider>
            </BrowserRouter>
          </TooltipProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </NativeDebugSplash>
  </ErrorBoundary>
);

export default App;
